import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink, Lock, Eye, EyeOff, Package, Heart, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { dbService } from '../lib/supabase';
import wand from '../assets/magic-wand.svg';

const CapsuleViewer = () => {
  const { id } = useParams();
  const [capsule, setCapsule] = useState(null);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCheatSheet, setSelectedCheatSheet] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showWelcomeLetter, setShowWelcomeLetter] = useState(false);
  const [cheatSheets, setCheatSheets] = useState([]);
    const [hasShownPortal, setHasShownPortal] = useState(false);
  
  // Animation states
  const [showPortal, setShowPortal] = useState(false);
  const [portalPhase, setPortalPhase] = useState('breathing'); // breathing, ripple, spiral, complete
  const [itemsVisible, setItemsVisible] = useState(false);
  const hasShownPortalRef = useRef(false); // Use ref instead of state to avoid re-renders

  useEffect(() => {
    loadCapsule();
  }, [id]);

  useEffect(() => {
    if (isAuthenticated && capsule) {
      loadCheatSheets();
    }
  }, [isAuthenticated, capsule]);

  // Portal animation sequence with debugging
  useEffect(() => {
    // Skip effect entirely if animation has already been shown
    if (hasShownPortal) {
      return;
    }

    console.log('🎭 Portal animation useEffect triggered:', {
      isAuthenticated,
      capsule: !!capsule,
      hasShownPortal,
      loading,
      activeTab,
      itemsCount: capsule?.items?.length || 0,
      cheatSheetsCount: cheatSheets?.length || 0
    });

    // Only trigger portal animation once when content is ready, regardless of how authentication happened
    if (isAuthenticated && capsule && !loading) {
      console.log('🎭 Checking if portal animation should trigger...');
      
      // Check if there's any content to show
      const hasItems = capsule.items && capsule.items.length > 0;
      const hasCheatSheetsContent = cheatSheets && cheatSheets.length > 0;
      
      console.log('🎭 Content check:', {
        hasItems,
        hasCheatSheetsContent,
        shouldTrigger: hasItems || hasCheatSheetsContent
      });
      
      if (hasItems || hasCheatSheetsContent) {
        console.log('🎭 TRIGGERING PORTAL ANIMATION');
        setShowPortal(true);
        
        // Phase 1: Breathing effect (2 seconds)
        const breathingTimer = setTimeout(() => {
          console.log('🎭 Portal phase: breathing -> ripple');
          setPortalPhase('ripple');
        }, 2000);
        
        // Phase 2: Ripple effect (1 second)
        const rippleTimer = setTimeout(() => {
          console.log('🎭 Portal phase: ripple -> spiral');
          setPortalPhase('spiral');
          setItemsVisible(true);
        }, 3000);
        
        // Phase 3: Complete transition (2 seconds)
        const completeTimer = setTimeout(() => {
          console.log('🎭 Portal phase: spiral -> complete');
          setPortalPhase('complete');
          setShowPortal(false);
          setHasShownPortal(true); // Mark as shown only after animation completes
        }, 5000);
        
        return () => {
          console.log('🎭 Cleaning up portal animation timers');
          clearTimeout(breathingTimer);
          clearTimeout(rippleTimer);
          clearTimeout(completeTimer);
        };
      } else {
        console.log('🎭 No content available, skipping portal animation');
      }
    } else {
      console.log('🎭 Portal animation conditions not met');
    }
  }, [isAuthenticated, capsule, cheatSheets, loading]); // Removed hasShownPortal from dependencies

  const loadCapsule = async () => {
    console.log('📦 Loading capsule...');
    setLoading(true);
    const { data, error } = await dbService.getCapsule(id);
    if (!error && data) {
      console.log('📦 Capsule loaded:', {
        name: data.name,
        hasPassword: !!data.password,
        isPublished: data.is_published,
        itemsCount: data.items?.length || 0
      });
      setCapsule(data);
      // If no password is set or capsule is not published, allow access
      if (!data.password || !data.is_published) {
        console.log('📦 Auto-authenticating (no password required)');
        setIsAuthenticated(true);
      }
    } else {
      console.error('📦 Error loading capsule:', error);
      setError('Capsule not found');
    }
    setLoading(false);
    console.log('📦 Loading complete');
  };

  const loadCheatSheets = async () => {
    console.log('📋 Loading cheat sheets...');
    const { data, error } = await dbService.getPublicCheatSheets(id);
    if (!error) {
      console.log('📋 Cheat sheets loaded:', data?.length || 0);
      setCheatSheets(data || []);
    } else {
      console.error('📋 Error loading cheat sheets:', error);
    }
  };

  const handlePasswordSubmit = (e) => {
    if (e) e.preventDefault();
    console.log('🔐 Password submitted');
    if (password === capsule.password) {
      console.log('🔐 Password correct, authenticating...');
      setIsAuthenticated(true);
      setError('');
    } else {
      console.log('🔐 Password incorrect');
      setError('Incorrect password. Please try again.');
    }
  };

  // Get all items from the capsule
  const getAllItems = () => {
    if (!capsule?.items) return [];
    
    // Items now belong directly to the capsule
    return capsule.items.map(item => ({
      ...item,
      // Find which outfits this item belongs to (if any)
      outfits: getOutfitsForItem(item.id)
    }));
  };

  // Find which outfits contain a specific item
  const getOutfitsForItem = (itemId) => {
    if (!capsule?.outfits) return [];
    
    const outfitsWithItem = [];
    capsule.outfits.forEach(outfit => {
      if (outfit.outfit_items?.some(oi => oi.items?.id === itemId)) {
        outfitsWithItem.push({
          id: outfit.id,
          name: outfit.name
        });
      }
    });
    
    return outfitsWithItem;
  };

  // Get unique categories
  const getCategories = () => {
    const allItems = getAllItems();
    const categories = [...new Set(allItems.map(item => item.category).filter(Boolean))];
    return categories.sort();
  };

  // Filter items by category
  const getFilteredItems = () => {
    const allItems = getAllItems();
    if (selectedCategory === 'all') return allItems;
    return allItems.filter(item => item.category === selectedCategory);
  };

  const handleItemClick = (item) => {
    const itemIndex = filteredItems.findIndex(i => i.id === item.id);
    setSelectedItem(item);
    setCurrentImageIndex(itemIndex);
  };

  const handleCheatSheetClick = (cheatSheet) => {
    setSelectedCheatSheet(cheatSheet);
  };

  const closeCarousel = () => {
    setSelectedItem(null);
    setCurrentImageIndex(0);
  };

  const closeCheatSheetModal = () => {
    setSelectedCheatSheet(null);
  };

  const nextItem = () => {
    const nextIndex = (currentImageIndex + 1) % filteredItems.length;
    setCurrentImageIndex(nextIndex);
    setSelectedItem(filteredItems[nextIndex]);
  };

  const prevItem = () => {
    const prevIndex = (currentImageIndex - 1 + filteredItems.length) % filteredItems.length;
    setCurrentImageIndex(prevIndex);
    setSelectedItem(filteredItems[prevIndex]);
  };

  // Sparkle effect on click
  useEffect(() => {
    const handleClick = (e) => {
      // Don't add sparkles to button clicks
      if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
        return;
      }
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      sparkle.style.left = `${e.clientX - 15}px`;
      sparkle.style.top = `${e.clientY - 15}px`;
      sparkle.innerHTML = '✨';
      document.body.appendChild(sparkle);
      setTimeout(() => sparkle.remove(), 600);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 font-light">Loading your style capsule...</p>
        </div>
      </div>
    );
  }

  if (error && !capsule) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 font-light">{error}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Floating Sparkles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute text-gray-300 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
                fontSize: `${10 + Math.random() * 6}px`
              }}
            >
              ✨
            </div>
          ))}
        </div>
        
        {/* Password Card */}
        <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
          <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md border border-gray-200">
            {/* Card Header */}
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              
              <h1 className="text-2xl font-light text-gray-900 mb-2">
                {capsule?.name || 'Style Capsule'}
              </h1>
              
              <p className="text-gray-600 font-light">
                Enter your password to view your personalized style capsule
              </p>
            </div>
            
            {/* Password Form */}
            <div className="space-y-6">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit(e)}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                </div>
              )}
              
              <button
                type="button"
                onClick={handlePasswordSubmit}
                className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                View Capsule
              </button>
            </div>
            
            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-xs text-gray-500 font-light">
                Curated by{' '}
                <span className="tracking-[0.1em]">kearaeliz</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasItems = capsule?.items && capsule.items.length > 0;
  const hasCheatSheets = cheatSheets && cheatSheets.length > 0;

  if (!hasItems && !hasCheatSheets) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 font-light">This capsule is still being prepared for you.</p>
        </div>
      </div>
    );
  }

  const filteredItems = getFilteredItems();
  const categories = getCategories();

  return (
    <div className="min-h-screen bg-white">
      {/* Portal Animation Overlay */}
      {showPortal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Dark Gradient Background */}
          <div 
            className={`absolute inset-0 transition-all duration-1000 ${
              portalPhase === 'breathing' ? 'scale-100 opacity-100' : 
              portalPhase === 'ripple' ? 'scale-105 opacity-90' : 
              'scale-110 opacity-0'
            }`}
            style={{
              background: 'radial-gradient(circle at center, rgba(0, 0, 0, 0.8) 0%, rgba(64, 64, 64, 0.9) 30%, rgba(0, 0, 0, 0.95) 100%)',
              filter: portalPhase === 'breathing' ? 'brightness(1.1)' : 'brightness(1.3)'
            }}
          />
          
          {/* Breathing Glow Effect */}
          <div 
            className={`absolute inset-0 transition-all duration-2000 ${
              portalPhase === 'breathing' ? 'opacity-30' : 'opacity-0'
            }`}
            style={{
              background: `radial-gradient(circle at center, 
                rgba(128, 128, 128, 0.4) 0%, 
                rgba(96, 96, 96, 0.3) 30%, 
                rgba(64, 64, 64, 0.2) 60%, 
                transparent 100%)`,
              animation: portalPhase === 'breathing' ? 'portalBreathe 3s ease-in-out infinite' : 'none'
            }}
          />
          
          {/* Ripple Effects */}
          {portalPhase === 'ripple' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="portal-ripple-1"></div>
              <div className="portal-ripple-2"></div>
              <div className="portal-ripple-3"></div>
            </div>
          )}
          
          {/* Magical Particles */}
          {(portalPhase === 'ripple' || portalPhase === 'spiral') && (
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute animate-ping"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${1 + Math.random() * 2}s`,
                    color: 'rgba(255, 255, 255, 0.8)'
                  }}
                >
                  ✨
                </div>
              ))}
            </div>
          )}
          
          {/* Portal Opening Text */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-1000 ${
            portalPhase === 'breathing' ? 'opacity-100' : 'opacity-0'
          }`}>
            <div className="text-center text-white drop-shadow-lg">
              <h2 className="text-3xl font-light mb-2 tracking-wide">
                Opening your wardrobe portal...
              </h2>
              <div className="w-12 h-1 bg-white mx-auto opacity-60 animate-pulse"></div>
              
              {/* Skip Button */}
              <button
                onClick={() => {
                  console.log('🎭 Portal animation skipped by user');
                  setPortalPhase('complete');
                  setShowPortal(false);
                  setItemsVisible(true);
                  setHasShownPortal(true); // Mark as shown when skipped
                }}
                className="mt-6 text-sm text-white/80 hover:text-white border border-white/50 px-4 py-2 rounded-full transition-all duration-300 hover:bg-white/10"
              >
                Skip Animation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-1000 ${
        showPortal ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
      }`}>
        {/* Header */}
        <header className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
            <div className="text-center">
              <div className="text-lg font-light text-gray-900 mb-6 tracking-[0.3em] uppercase">
                kearaeliz
              </div>
              <h1 className="text-4xl sm:text-5xl font-light text-gray-900 mb-4">
                {capsule.name}
              </h1>
              {capsule.description && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowWelcomeLetter(true)}
                    className="bg-gray-900 text-white px-6 py-3 hover:bg-gray-800 transition-colors font-light tracking-wide text-sm rounded cursor-pointer"
                  >
                    Welcome Letter
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="border-b border-gray-100">
            <div className="flex justify-center space-x-12">
              {hasItems && (
                <button
                  onClick={() => {
                    console.log('🔄 Switched to items tab');
                    setActiveTab('items');
                  }}
                  className={`py-4 text-sm font-light tracking-wide transition-all duration-300 ${
                    activeTab === 'items'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Items
                </button>
              )}
              {hasCheatSheets && (
                <button
                  onClick={() => {
                    console.log('🔄 Switched to cheatsheets tab');
                    setActiveTab('cheatsheets');
                  }}
                  className={`py-4 text-sm font-light tracking-wide transition-all duration-300 ${
                    activeTab === 'cheatsheets'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Cheat Sheets
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Items Tab */}
          {activeTab === 'items' && hasItems && (
            <>
              {/* Category Filters */}
              <div className="mb-12">
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 sm:gap-8">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`text-sm font-light tracking-wide transition-all duration-300 ${
                      selectedCategory === 'all'
                        ? 'text-gray-900 border-b-2 border-gray-900 pb-1'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    All Items
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`text-sm font-light tracking-wide capitalize transition-all duration-300 ${
                        selectedCategory === category
                          ? 'text-gray-900 border-b-2 border-gray-900 pb-1'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Items Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8 px-4">
                {filteredItems.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    onClick={() => handleItemClick(item)}
                    className="group cursor-pointer"
                    style={{
                      opacity: itemsVisible ? 1 : 0,
                      transform: itemsVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)',
                      transition: `all 0.8s ease-out ${index * 0.1}s`,
                      animation: itemsVisible && portalPhase === 'spiral' ? 
                        `spiralIn 1.5s ease-out ${index * 0.15}s both` : 
                        `fadeIn 0.6s ease-out ${index * 0.1}s both`
                    }}
                  >
                    <div className="aspect-square bg-[#F4EEEE] mb-4 overflow-hidden p-2">
                      <img
                        src={item.photo_url}
                        alt={item.name}
                        className="w-full h-full object-contain group-hover:opacity-80 transition-opacity duration-300"
                      />
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-sm font-light text-gray-900 mb-1">
                        {item.name}
                      </h3>
                      {item.price && (
                        <p className="text-sm font-light text-gray-600">
                          {item.price}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {filteredItems.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-500 font-light">No items found in this category.</p>
                </div>
              )}
            </>
          )}

          {/* Cheat Sheets Tab */}
          {activeTab === 'cheatsheets' && hasCheatSheets && (
            <>
             

              {/* Cheat Sheets Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
                {cheatSheets.map((cheatSheet, index) => (
                  <div
                    key={cheatSheet.id}
                    onClick={() => handleCheatSheetClick(cheatSheet)}
                    className="group cursor-pointer"
                    style={{
                      opacity: itemsVisible ? 1 : 0,
                      transform: itemsVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)',
                      transition: `all 0.8s ease-out ${index * 0.1}s`,
                      animation: itemsVisible && portalPhase === 'spiral' ? 
                        `spiralIn 1.5s ease-out ${index * 0.15}s both` : 
                        `fadeIn 0.6s ease-out ${index * 0.1}s both`
                    }}
                  >
                    <div className="aspect-square bg-[#F4EEEE] mb-4 overflow-hidden">
                      {cheatSheet.image_url ? (
                        <img
                          src={cheatSheet.image_url}
                          alt={cheatSheet.name}
                          className="w-full h-full object-cover group-hover:opacity-80 transition-opacity duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Package className="w-12 h-12" />
                        </div>
                      )}
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-lg font-light text-gray-900 mb-2">
                        {cheatSheet.name}
                      </h3>
                      {cheatSheet.description && (
                        <p className="text-sm font-light text-gray-600 mb-2">
                          {cheatSheet.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 font-light">
                        {cheatSheet.cheat_sheet_items?.length || 0} pieces
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Welcome Letter Modal */}
      {showWelcomeLetter && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowWelcomeLetter(false)}
          style={{ zIndex: 9999 }}
        >
          <div 
            className="bg-transparent max-w-3xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowWelcomeLetter(false)}
              className="absolute top-4 right-4 z-20 bg-white hover:bg-gray-50 p-2 rounded-full shadow-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
            
            {/* Paper Card */}
            <div className="paper-card">
              <div className="paper-content">
                <p className="script-text">
                  {capsule.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Carousel Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Navigation arrows */}
            <button
              onClick={prevItem}
              className="absolute left-6 top-1/2 transform -translate-y-1/2 z-10 bg-white hover:bg-gray-50 p-3 transition-colors duration-200"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            
            <button
              onClick={nextItem}
              className="absolute right-6 top-1/2 transform -translate-y-1/2 z-10 bg-white hover:bg-gray-50 p-3 transition-colors duration-200"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>

            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <div>
                <h2 className="text-2xl font-light text-gray-900 mb-1">{selectedItem.name}</h2>
                {selectedItem.brand && (
                  <p className="text-gray-600 font-light">{selectedItem.brand}</p>
                )}
              </div>
              <div className="flex items-center space-x-6">
                <span className="text-sm text-gray-500 font-light">
                  {currentImageIndex + 1} of {filteredItems.length}
                </span>
                <button
                  onClick={closeCarousel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {/* Image */}
                <div className="relative">
                  <div className="aspect-square bg-[#F4EEEE] overflow-hidden">
                    <img
                      src={selectedItem.photo_url}
                      alt={selectedItem.name}
                      className="w-full h-full object-contain transition-opacity duration-300"
                    />
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-8">
                  {selectedItem.price && (
                    <div>
                      <p className="text-2xl font-light text-gray-900">{selectedItem.price}</p>
                    </div>
                  )}

                  {selectedItem.description && (
                    <div>
                      <h3 className="text-lg font-light text-gray-900 mb-3">Description</h3>
                      <p className="text-gray-700 leading-relaxed font-light">{selectedItem.description}</p>
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-light text-gray-900 mb-3">Details</h3>
                    <div className="space-y-2">
                      {selectedItem.category && (
                        <p className="text-sm text-gray-600 font-light">
                          <span className="font-normal">Category:</span> {selectedItem.category}
                        </p>
                      )}
                      {selectedItem.brand && (
                        <p className="text-sm text-gray-600 font-light">
                          <span className="font-normal">Brand:</span> {selectedItem.brand}
                        </p>
                      )}
                      {selectedItem.color && (
                        <p className="text-sm text-gray-600 font-light">
                          <span className="font-normal">Color:</span> {selectedItem.color}
                        </p>
                      )}
                      {selectedItem.size && (
                        <p className="text-sm text-gray-600 font-light">
                          <span className="font-normal">Size:</span> {selectedItem.size}
                        </p>
                      )}
                      {selectedItem.outfits && selectedItem.outfits.length > 0 && (
                        <p className="text-sm text-gray-600 font-light">
                          <span className="font-normal">Featured in:</span> {selectedItem.outfits.map(outfit => outfit.name).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>

                  {selectedItem.link && (
                    <div>
                      <a
                        href={selectedItem.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-gray-900 text-white px-8 py-3 hover:bg-gray-800 transition-colors font-light tracking-wide"
                      >
                        <span>Shop Now</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Thumbnail navigation */}
            <div className="px-8 pb-8">
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {filteredItems.map((item, index) => (
                  <button
                    key={`thumb-${item.id}-${index}`}
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setSelectedItem(item);
                    }}
                    className={`flex-shrink-0 w-16 h-16 overflow-hidden transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'ring-1 ring-gray-900 ring-offset-2'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={item.photo_url}
                      alt={item.name}
                      className="w-full h-full object-contain"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cheat Sheet Detail Modal */}
      {selectedCheatSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <div>
                <h2 className="text-2xl font-light text-gray-900 mb-1">{selectedCheatSheet.name}</h2>
                {selectedCheatSheet.description && (
                  <p className="text-gray-600 font-light">{selectedCheatSheet.description}</p>
                )}
              </div>
              <button
                onClick={closeCheatSheetModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Cheat Sheet Image */}
                <div className="relative">
                  <div className="aspect-square bg-[#F4EEEE] overflow-hidden rounded-lg">
                    {selectedCheatSheet.image_url ? (
                      <img
                        src={selectedCheatSheet.image_url}
                        alt={selectedCheatSheet.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Package className="w-16 h-16" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Featured Items */}
                <div>
                  <h3 className="text-lg font-light text-gray-900 mb-6">Featured Items</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedCheatSheet.cheat_sheet_items?.map((cheatSheetItem) => (
                      <div
                        key={cheatSheetItem.id}
                        onClick={() => handleItemClick(cheatSheetItem.items)}
                        className="group cursor-pointer"
                      >
                        <div className="aspect-square bg-[#F4EEEE] overflow-hidden rounded-lg mb-2 p-2">
                          <img
                            src={cheatSheetItem.items.photo_url}
                            alt={cheatSheetItem.items.name}
                            className="w-full h-full object-contain group-hover:opacity-80 transition-opacity duration-300"
                          />
                        </div>
                        <div className="text-center">
                          <h4 className="text-sm font-light text-gray-900 mb-1">
                            {cheatSheetItem.items.name}
                          </h4>
                          {cheatSheetItem.items.brand && (
                            <p className="text-xs text-gray-600 font-light">
                              {cheatSheetItem.items.brand}
                            </p>
                          )}
                          {cheatSheetItem.items.price && (
                            <p className="text-xs text-gray-600 font-light">
                              {cheatSheetItem.items.price}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-100 py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Heart className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-light tracking-[0.2em] text-gray-600">
              Styled with love by kearaeliz
            </span>
          </div>
          <p className="text-gray-500 text-sm font-light mb-6">
            Questions about your capsule? Reach out anytime.
          </p>
          <div className="space-x-8">
            <a
              href="https://substack.com/@kearated"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-900 transition-colors text-sm font-light underline"
            >
              Substack
            </a>
            <a
              href="https://shopmy.us/kearaelizabeth"
              className="text-gray-500 hover:text-gray-900 transition-colors text-sm font-light underline"
            >
              ShopMy
            </a>
            <a
              href="mailto:kearaelizabeth.lifestyle@gmail.com"
              className="text-gray-500 hover:text-gray-900 transition-colors text-sm font-light underline"
            >
              Email
            </a>
          </div>
          <p className="mt-6 text-xs text-gray-400 font-light">
            Some links may be affiliate commissionable.
          </p>
        </div>
      </footer>

      {/* Custom CSS for animations and wand cursor */}
      <style>{`
        * {
          cursor: url("${wand}") 6 6, auto;
        }
        
        button, a, [role="button"] {
          cursor: url("${wand}") 6 6, pointer;
        }
        
        input {
          cursor: url("${wand}") 6 6, text;
        }
        
        /* Paper card styles */
        .paper-card {
          background: linear-gradient(135deg, #fefefe 0%, #f8f8f8 100%);
          border-radius: 8px;
          box-shadow: 
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
          position: relative;
          overflow: hidden;
        }
        
        .paper-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 50%, rgba(0, 0, 0, 0.02) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.02) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 0, 0, 0.02) 0%, transparent 50%);
          pointer-events: none;
        }
        
        .paper-content {
          padding: 3rem 2.5rem;
          position: relative;
          z-index: 1;
        }
        
        .script-text {
          font-family: 'Great Vibes', 'Allura', 'Dancing Script', cursive;
          font-size: 1.5rem;
          line-height: 1.8;
          color: #2d3748;
          text-align: center;
          font-weight: 400;
          letter-spacing: 0.5px;
        }
        
        @media (max-width: 640px) {
          .paper-content {
            padding: 2rem 1.5rem;
          }
          
          .script-text {
            font-size: 1.25rem;
            line-height: 1.6;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spiralIn {
          0% {
            opacity: 0;
            transform: translateX(50vw) translateY(50vh) rotate(720deg) scale(0.1);
          }
          60% {
            opacity: 0.8;
            transform: translateX(0) translateY(0) rotate(360deg) scale(1.1);
          }
          100% {
            opacity: 1;
            transform: translateX(0) translateY(0) rotate(0deg) scale(1);
          }
        }
        
        @keyframes portalBreathe {
          0%, 100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.5;
          }
        }
        
        @keyframes portalRipple {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(4);
            opacity: 0;
          }
        }
        
        .portal-ripple-1, .portal-ripple-2, .portal-ripple-3 {
          position: absolute;
          width: 200px;
          height: 200px;
          border: 2px solid rgba(255, 255, 255, 0.6);
          border-radius: 50%;
          animation: portalRipple 2s ease-out infinite;
        }
        
        .portal-ripple-1 {
          animation-delay: 0s;
          border-color: rgba(255, 255, 255, 0.8);
        }
        
        .portal-ripple-2 {
          animation-delay: 0.5s;
          border-color: rgba(192, 192, 192, 0.6);
        }
        
        .portal-ripple-3 {
          animation-delay: 1s;
          border-color: rgba(128, 128, 128, 0.4);
        }
        
        @keyframes sparkle {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.5) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) rotate(360deg);
            opacity: 0;
          }
        }
        
        .sparkle {
          position: fixed;
          pointer-events: none;
          z-index: 9999;
          font-size: 20px;
          animation: sparkle 0.6s ease-out forwards;
        }
        
        /* Portal magic particles */
        @keyframes magicFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }
        
        .magic-particle {
          animation: magicFloat 3s ease-in-out infinite;
        }
        
        /* Responsive adjustments */
        @media (max-width: 480px) {
          .portal-ripple-1, .portal-ripple-2, .portal-ripple-3 {
            width: 150px;
            height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default CapsuleViewer;
